import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { t, font, Button } from "@realtyflows/ui";
import { getPacket, getPacketForm, savePacketFormFields, markPacketFormComplete, previewPacketForm } from "../api";
import useResponsive from "../hooks/useResponsive";

function AutoSaveIndicator({ status }) {
  const color = status === "saved" ? t.emerald : status === "saving" ? t.amber : t.textMuted;
  const label = status === "saved" ? "Saved" : status === "saving" ? "Saving..." : "Unsaved";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: color, transition: "background 0.3s" }} />
      <span style={{ fontSize: 11, color, fontFamily: font.mono, fontWeight: 600 }}>{label}</span>
    </div>
  );
}

function SharedBadge() {
  return (
    <span style={{
      fontSize: 9, fontWeight: 600, color: t.gold, background: `${t.gold}15`, padding: "2px 6px",
      borderRadius: 4, fontFamily: font.body, marginLeft: 6, letterSpacing: "0.5px",
    }}>SHARED</span>
  );
}

function FormField({ field, value, onChange, isShared }) {
  const baseStyle = {
    width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 14,
    fontFamily: font.body, color: t.textPrimary, outline: "none", transition: "border-color 0.2s",
    border: `1.5px solid ${isShared ? t.gold + "40" : t.border}`,
    background: isShared ? `${t.gold}08` : t.surface,
  };

  const onFocus = (e) => { e.target.style.borderColor = isShared ? t.gold : t.emerald; };
  const onBlur = (e) => { e.target.style.borderColor = isShared ? t.gold + "40" : t.border; };

  if (field.type === "textarea") {
    return <textarea value={value} onChange={e => onChange(e.target.value)} rows={3}
      style={{ ...baseStyle, resize: "vertical" }} onFocus={onFocus} onBlur={onBlur} />;
  }
  if (field.type === "select" && field.options) {
    return (
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ ...baseStyle, cursor: "pointer" }} onFocus={onFocus} onBlur={onBlur}>
        <option value="">Select...</option>
        {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    );
  }
  if (field.type === "checkbox") {
    return (
      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
        <input type="checkbox" checked={value === "true" || value === true}
          onChange={e => onChange(e.target.checked ? "true" : "false")}
          style={{ width: 18, height: 18, accentColor: t.emerald }} />
        <span style={{ fontSize: 13, color: t.textSecondary, fontFamily: font.body }}>{field.label}</span>
      </label>
    );
  }

  return <input type={field.type === "date" ? "date" : field.type === "number" ? "number" : "text"}
    value={value} onChange={e => onChange(e.target.value)} placeholder={field.label}
    style={baseStyle} onFocus={onFocus} onBlur={onBlur} />;
}

export default function PacketFiller() {
  const { id: packetId } = useParams();
  const navigate = useNavigate();
  const responsive = useResponsive();
  const isMobile = responsive.width < 640;

  const [packet, setPacket] = useState(null);
  const [activeFormIdx, setActiveFormIdx] = useState(0);
  const [formData, setFormData] = useState(null); // current form's resolved fields + schema
  const [fields, setFields] = useState({});
  const [saveStatus, setSaveStatus] = useState("saved");
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [previewHtml, setPreviewHtml] = useState(null);
  const saveTimeoutRef = useRef(null);
  const sharedKeysRef = useRef(new Set());

  // Load packet
  useEffect(() => {
    getPacket(packetId).then(p => {
      setPacket(p);
      setLoading(false);
    }).catch(() => navigate("/packets"));
  }, [packetId, navigate]);

  // Load active form data
  const loadForm = useCallback(async (formIdx) => {
    if (!packet || !packet.forms[formIdx]) return;
    const pf = packet.forms[formIdx];
    const data = await getPacketForm(packetId, pf.id);
    setFormData(data);
    setFields(data.resolved_fields || {});
    // Track which keys are shared
    const shared = new Set();
    (data.field_schema || []).forEach(f => { if (f.shared) shared.add(f.key); });
    sharedKeysRef.current = shared;
    setSaveStatus("saved");
  }, [packet, packetId]);

  useEffect(() => {
    if (packet) loadForm(activeFormIdx);
  }, [packet, activeFormIdx, loadForm]);

  // Auto-save with 500ms debounce
  const doSave = useCallback(async (key, value) => {
    if (!packet || !formData) return;
    setSaveStatus("saving");
    const isShared = sharedKeysRef.current.has(key);
    try {
      await savePacketFormFields(packetId, formData.id, {
        fields: isShared ? {} : { [key]: value },
        shared_field_updates: isShared ? { [key]: value } : undefined,
      });
      // Refresh packet shared_fields if shared
      if (isShared) {
        const updated = await getPacket(packetId);
        setPacket(updated);
      }
      setSaveStatus("saved");
    } catch {
      setSaveStatus("unsaved");
    }
  }, [packetId, packet, formData]);

  const onFieldChange = (key, value) => {
    setFields(prev => ({ ...prev, [key]: value }));
    setSaveStatus("unsaved");
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => doSave(key, value), 500);
  };

  const goToForm = (idx) => {
    if (idx >= 0 && idx < (packet?.forms?.length || 0)) {
      setActiveFormIdx(idx);
      if (isMobile) setSidebarOpen(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!formData) return;
    const newState = !formData.is_complete;
    await markPacketFormComplete(packetId, formData.id, newState);
    // Refresh
    const updated = await getPacket(packetId);
    setPacket(updated);
    setFormData(prev => ({ ...prev, is_complete: newState }));
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", fontFamily: font.body, color: t.textMuted }}>
      Loading packet...
    </div>
  );

  if (!packet) return null;

  const forms = packet.forms || [];
  const activeForm = forms[activeFormIdx];
  const completedCount = forms.filter(f => f.is_complete).length;
  const progress = forms.length > 0 ? Math.round((completedCount / forms.length) * 100) : 0;

  return (
    <div style={{ display: "flex", height: "100%", background: t.pageBg, overflow: "hidden" }}>

      {/* Sidebar — form list */}
      {(sidebarOpen || !isMobile) && (
        <div style={{
          width: isMobile ? "100%" : 280, minWidth: isMobile ? undefined : 280,
          background: t.surface, borderRight: `1px solid ${t.border}`,
          display: "flex", flexDirection: "column", overflow: "hidden",
          ...(isMobile ? { position: "absolute", inset: 0, zIndex: 20 } : {}),
        }}>
          {/* Sidebar header */}
          <div style={{ padding: "16px 16px 12px", borderBottom: `1px solid ${t.border}` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, fontFamily: font.mono, letterSpacing: "1px", textTransform: "uppercase" }}>
                Forms ({completedCount}/{forms.length})
              </span>
              {isMobile && (
                <button onClick={() => setSidebarOpen(false)} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: t.textMuted }}>&times;</button>
              )}
            </div>
            {/* Progress bar */}
            <div style={{ height: 4, background: t.border, borderRadius: 2, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${progress}%`, background: t.emerald, borderRadius: 2, transition: "width 0.3s" }} />
            </div>
          </div>

          {/* Form list */}
          <div style={{ flex: 1, overflowY: "auto", padding: "8px 8px" }}>
            {forms.map((f, i) => (
              <div key={f.id} onClick={() => goToForm(i)} style={{
                padding: "10px 12px", borderRadius: 8, cursor: "pointer", marginBottom: 4,
                background: i === activeFormIdx ? `${t.gold}12` : "transparent",
                border: i === activeFormIdx ? `1.5px solid ${t.gold}40` : "1.5px solid transparent",
                transition: "all 0.15s",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10, fontWeight: 700, fontFamily: font.mono,
                    background: f.is_complete ? t.emerald : t.border,
                    color: f.is_complete ? "#fff" : t.textMuted,
                  }}>{f.is_complete ? "\u2713" : i + 1}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontSize: 12, fontWeight: i === activeFormIdx ? 700 : 500,
                      color: i === activeFormIdx ? t.navyLight : t.textPrimary,
                      fontFamily: font.body, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>{f.form_name}</div>
                    <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body, textTransform: "capitalize" }}>{f.category}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Sidebar footer */}
          <div style={{ padding: 12, borderTop: `1px solid ${t.border}` }}>
            <Button variant="primary" fullWidth onClick={() => navigate(`/packets/${packetId}`)}>
              {progress === 100 ? "Review & Send" : "Back to Packet"}
            </Button>
          </div>
        </div>
      )}

      {/* Main — form editor */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Top bar */}
        <div style={{
          padding: isMobile ? "10px 12px" : "12px 24px",
          borderBottom: `1px solid ${t.border}`, background: t.surface,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            {isMobile && (
              <button onClick={() => setSidebarOpen(true)} style={{
                background: "none", border: `1px solid ${t.border}`, borderRadius: 6, padding: "6px 8px",
                fontSize: 12, cursor: "pointer", color: t.textMuted, fontFamily: font.body,
              }}>{completedCount}/{forms.length}</button>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 600, color: t.navyLight, fontFamily: font.display, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {formData?.form_name || "Loading..."}
              </div>
              <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
                Form {activeFormIdx + 1} of {forms.length} — {packet.name}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
            <AutoSaveIndicator status={saveStatus} />
            <button onClick={async () => {
              if (!formData) return;
              try { const data = await previewPacketForm(packetId, formData.id); setPreviewHtml(data.html); } catch {}
            }} style={{
              padding: "6px 14px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer",
              fontFamily: font.body, background: "transparent", border: `1.5px solid ${t.border}`,
              color: t.textSecondary, transition: "all 0.2s",
            }}>Preview PDF</button>
            <button onClick={handleMarkComplete} style={{
              padding: "6px 14px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer",
              fontFamily: font.body, letterSpacing: "0.5px", transition: "all 0.2s",
              background: formData?.is_complete ? t.emerald : "transparent",
              color: formData?.is_complete ? "#fff" : t.emerald,
              border: `1.5px solid ${t.emerald}`,
            }}>{formData?.is_complete ? "\u2713 Complete" : "Mark Complete"}</button>
          </div>
        </div>

        {/* Form fields */}
        <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 16 : 32 }}>
          <div style={{ maxWidth: 680, margin: "0 auto" }}>
            {formData?.description && (
              <p style={{ fontSize: 13, color: t.textMuted, fontFamily: font.body, marginBottom: 24, lineHeight: 1.6 }}>
                {formData.description}
              </p>
            )}

            {(formData?.field_schema || []).map(field => {
              if (field.type === "checkbox") {
                return (
                  <div key={field.key} style={{ marginBottom: 16 }}>
                    <FormField field={field} value={fields[field.key] || ""} onChange={v => onFieldChange(field.key, v)}
                      isShared={field.shared} />
                  </div>
                );
              }
              return (
                <div key={field.key} style={{ marginBottom: 20 }}>
                  <label style={{
                    display: "flex", alignItems: "center", fontSize: 12, fontWeight: 600,
                    color: t.textSecondary, fontFamily: font.body, marginBottom: 6,
                  }}>
                    {field.label}
                    {field.required && <span style={{ color: t.coral, marginLeft: 3 }}>*</span>}
                    {field.shared && <SharedBadge />}
                  </label>
                  <FormField field={field} value={fields[field.key] || ""} onChange={v => onFieldChange(field.key, v)}
                    isShared={field.shared} />
                </div>
              );
            })}

            {(!formData?.field_schema || formData.field_schema.length === 0) && (
              <div style={{ textAlign: "center", padding: "60px 20px", color: t.textMuted, fontFamily: font.body }}>
                This form has no fillable fields.
              </div>
            )}
          </div>
        </div>

        {/* Bottom navigation — Back / Next */}
        <div style={{
          padding: isMobile ? "12px 16px" : "14px 24px",
          borderTop: `1px solid ${t.border}`, background: t.surface,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <button onClick={() => goToForm(activeFormIdx - 1)} disabled={activeFormIdx === 0} style={{
            padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: activeFormIdx === 0 ? "not-allowed" : "pointer",
            fontFamily: font.body, background: "transparent", border: `1.5px solid ${t.border}`,
            color: activeFormIdx === 0 ? t.textMuted : t.textPrimary, opacity: activeFormIdx === 0 ? 0.5 : 1,
            transition: "all 0.2s",
          }}>
            &larr; Back
          </button>

          <span style={{ fontSize: 11, color: t.textMuted, fontFamily: font.mono }}>
            {activeFormIdx + 1} / {forms.length}
          </span>

          {activeFormIdx < forms.length - 1 ? (
            <button onClick={() => goToForm(activeFormIdx + 1)} style={{
              padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
              fontFamily: font.body, background: t.navyLight, border: "none", color: "#fff",
              transition: "all 0.2s",
            }}>
              Next &rarr;
            </button>
          ) : (
            <Button variant="primary" onClick={() => navigate(`/packets/${packetId}`)}>
              Review & Send
            </Button>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {previewHtml && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }} onClick={() => setPreviewHtml(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "#fff", borderRadius: 16, width: isMobile ? "100%" : 800,
            maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden",
            boxShadow: "0 24px 48px rgba(0,0,0,0.3)",
          }}>
            <div style={{
              padding: "14px 20px", borderBottom: `1px solid ${t.border}`,
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: t.navyLight, fontFamily: font.display }}>
                {formData?.form_name} — Preview
              </div>
              <button onClick={() => setPreviewHtml(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: t.textMuted }}>&times;</button>
            </div>
            <iframe
              srcDoc={previewHtml}
              title="Form Preview"
              style={{ flex: 1, border: "none", minHeight: isMobile ? 400 : 600, width: "100%" }}
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      )}
    </div>
  );
}
